/*
    this component was already existing in the codebase,
    we've made changes into it so we can reuse it on the new search overview page
    after we will finish the migration we can remove the old component
    and move this one to the correct location
*/

import { colorsPalettes } from "@similarweb/styles";
import { swSettings } from "app/common/services/swSettings";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import dailyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/dailyIntervalConfig";
import monthlyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/monthlyIntervalConfig";
import weeklyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/weeklyIntervalConfig";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import "pro-features/components/Chart/styles/sharedTooltip.scss";
import { abbrNumberFilter } from "app/filters/ngFilters";
import dayjs from "dayjs";
import { changeTooltipFormatter } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Components/ChangeTooltip";
import ChangeBellWithoutTooltip from "pro-features/components/Chart/src/configs/plotLines/ChangeBellWithoutTooltip";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type { IDataType } from "app/components/React/GraphTypeSwitcher/GraphTypeSwitcher";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import { EGraphTabs } from "../../../tabs/SearchOverviewGraph/Helpers/SearchOverviewGraphConfig";
import { apiFormat } from "app/services/DurationService";

export interface ICategory {
    id: string;
    title: string;
}

interface ISearchTrafficGraphTabConfig {
    name: string;
    title: string;
    iconName: string;
    format: string;
    tooltip: string;
    identifier: EGraphTabs;
    eventName: string;
    metric: string;
    id: number;
    value: number;
    key: string;
}

const getIntervalConfig = (timeGranularity) => {
    if (timeGranularity === timeGranularityList.monthly) {
        return monthlyIntervalConfig;
    }
    if (timeGranularity === timeGranularityList.weekly) {
        return weeklyIntervalConfig;
    }
    return dailyIntervalConfig;
};

const getBaseChartConfig = (
    filter,
    metricName,
    yFormatter,
    isPercents = false,
    contextProps,
    getChangeColor = null,
) => {
    const { isWindow, duration, granularity, durationObj, isMonthsToDateActive, webSource, rawData } = contextProps;
    const timeGranularity = Object.values(timeGranularityList).find(({ name }) => name === String(granularity));
    const format = timeGranularity === timeGranularityList.monthly ? "MMM YY" : "DD MMM";
    const { to, from } = durationObj.forAPI;
    const lastSupportedDate = isMonthsToDateActive ? swSettings.current.lastSupportedDailyDate : to.replace(/\|/g, "/");
    const currentGranularity = getIntervalConfig(timeGranularity);
    const yAxisFormatter = ({ value }) => abbrNumberFilter()(value);
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format(format);
    const newMMXAlgoStartDate = dayjs(swSettings.current.resources.NewAlgoMMX);
    return combineConfigs({ yAxisFormatter, xAxisFormatter }, [
        currentGranularity,
        noLegendConfig,
        yAxisLabelsConfig,
        xAxisLabelsConfig,
        xAxisCrosshair,
        tooltipContainerConfig,
        webSource !== devicesTypes.DESKTOP &&
            rawData &&
            from !== newMMXAlgoStartDate.format(apiFormat) &&
            ChangeBellWithoutTooltip(newMMXAlgoStartDate),
        {
            chart: {
                height: 295,
                spacingTop: 10,
                plotBackgroundColor: "transparent",
                events: {},
                style: {
                    fontFamily: "Roboto",
                },
            },
            tooltip: {
                followPointer: false,
                shared: true,
                useHTML: true,
                formatter: changeTooltipFormatter(
                    metricName,
                    yFormatter,
                    isWindow,
                    duration,
                    isPercents,
                    granularity,
                    lastSupportedDate,
                    getChangeColor,
                ),
            },
            plotOptions: {
                line: {
                    marker: {
                        enabled: timeGranularity !== timeGranularityList.daily,
                        symbol: "circle",
                    },
                    lineWidth: 2,
                    connectNulls: true,
                },
                area: {
                    marker: {
                        enabled: false,
                        symbol: "circle",
                    },
                    stacking: "normal",
                },
                series: {
                    fillOpacity: 1,
                    animation: false,
                    turboThreshold: 2000,
                },
            },
            yAxis: {
                min: 0,
                gridLineWidth: 0.5,
                showFirstLabel: true,
                showLastLabel: true,
                reversed: false,
                gridZIndex: 2,
                reversedStacks: true,
                tickPixelInterval: 50,
                plotLines: [
                    {
                        color: "#656565",
                        width: 2,
                        value: 0,
                    },
                ],
                labels: {
                    style: {
                        textTransform: "uppercase",
                        fontSize: "11px",
                        fontFamily: "Roboto",
                        color: "#919191",
                    },
                    formatter: yFormatter,
                },
            },
            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                tickLength: 5,
                labels: {
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: "#919191",
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                maxPadding: 0,
            },
        },
    ]);
};

export const searchTrafficGraphTabsConfig: ISearchTrafficGraphTabConfig[] = [
    {
        name: "TrafficShare",
        title: "search.channelanalysis.traffic-share.title",
        iconName: "avg-visit-duration",
        format: "minVisitsAbbr",
        tooltip: "wa.ao.graph.trafficshare.tooltip",
        identifier: EGraphTabs.TrafficShare,
        eventName: "Search Traffic",
    },
    {
        name: "PPCSpent",
        title: "search.channelanalysis.ppc-spent.title",
        iconName: "spend",
        format: "minVisitsAbbr",
        tooltip: "wa.ao.graph.ppcspent.tooltip",
        identifier: EGraphTabs.PpcSpend,
        eventName: "PPC Spend",
    },
    {
        name: "CostPerVisit",
        title: "search.channelanalysis.cost-per-visit.title",
        iconName: "revenue",
        format: "minVisitsAbbr",
        tooltip: "wa.ao.graph.costpervisit.tooltip",
        identifier: EGraphTabs.CostPerVisit,
        eventName: "Cost Per Visit",
    },
].map<ISearchTrafficGraphTabConfig>((item, index) => {
    return {
        ...item,
        metric: item.name,
        id: index,
        value: 0,
        key: `tab-${index}`,
    };
});

export const timeGranularityList = {
    daily: { title: "D", disabled: false, name: "Daily", index: 0 },
    weekly: { title: "W", disabled: false, name: "Weekly", index: 1 },
    monthly: { title: "M", disabled: false, name: "Monthly", index: 2 },
};

const organicPaid = "organicPaid";
const brandedNonBranded = "brandedNonBranded";
const searchType = "searchType";
export const MMX_ORGANIC_PAID = "MMXOrganicPaid";

export const ORGANIC_PAID_CATEGORY = {
    id: organicPaid,
    title: "analysis.source.search.overview.filters.organicpaid",
};
export const BRANDED_NON_BRANDED_CATEGORY = {
    id: brandedNonBranded,
    title: "analysis.source.search.overview.filters.brandednonbranded",
};
export const SEARCH_TYPE_CATEGORY = {
    id: searchType,
    title: "analysis.source.search.overview.filters.searchType",
};

export const MMX_ORGANIC_PAID_CATEGORY = {
    id: MMX_ORGANIC_PAID,
    title: "analysis.source.search.overview.filters.organicpaid",
};

export const availableCategories = [
    ORGANIC_PAID_CATEGORY,
    MMX_ORGANIC_PAID_CATEGORY,
    BRANDED_NON_BRANDED_CATEGORY,
    SEARCH_TYPE_CATEGORY,
];
export enum legendTypes {
    OrganicSearch = "Organic Search",
    PaidSearch = "Paid Search",
    AllChannels = "All Channels",
    Branded = "Branded",
    NonBranded = "Non Branded",
    RegularSearch = "Regular Search",
    NewsSearch = "News Search",
    ImageSearch = "Image Search",
    VideoSearch = "Video Search",
    ShoppingSearch = "Shopping Search",
    MapsSearch = "Maps Search",
}

const singleModeLegends = {
    organicPaid: [legendTypes.OrganicSearch, legendTypes.PaidSearch, legendTypes.AllChannels],
    MMXOrganicPaid: [legendTypes.OrganicSearch, legendTypes.PaidSearch, legendTypes.AllChannels],
    brandedNonBranded: [legendTypes.Branded, legendTypes.NonBranded],
    searchType: [
        legendTypes.RegularSearch,
        legendTypes.NewsSearch,
        legendTypes.ImageSearch,
        legendTypes.VideoSearch,
        legendTypes.ShoppingSearch,
        legendTypes.MapsSearch,
    ],
};

const legendsAndChannelsObjects = {
    [legendTypes.AllChannels]: {
        color: colorsPalettes.bluegrey["600"],
        text: "analysis.source.search.overview.filters.all.channels",
    },
    "All Duration": {
        color: colorsPalettes.bluegrey["600"],
        text: "analysis.source.search.overview.filters.all.duration",
    },
    "All page visits": {
        color: colorsPalettes.bluegrey["600"],
        text: "analysis.source.search.overview.filters.all.page.visits",
    },
    "Organic Search": {
        color: colorsPalettes.bluegrey["600"],
        text: "analysis.source.search.overview.filters.organic",
    },
    "Search Traffic": {
        color: colorsPalettes.bluegrey["600"],
        text: "analysis.source.search.overview.filters.mobileweb_legend",
    },
    "Paid Search": {
        color: colorsPalettes.torquoise["300"],
        text: "analysis.source.search.overview.filters.paid",
    },
    Branded: {
        color: colorsPalettes.bluegrey["600"],
        text: "analysis.source.search.overview.filters.branded",
    },
    "Non Branded": {
        color: colorsPalettes.torquoise["300"],
        text: "analysis.source.search.overview.filters.non-branded",
    },
    "Regular Search": {
        color: colorsPalettes.bluegrey["600"],
        text: "analysis.source.search.overview.filters.regular-search",
    },
    "Image Search": {
        color: colorsPalettes.orange["300"],
        text: "analysis.source.search.overview.filters.image-search",
    },
    "Maps Search": {
        color: colorsPalettes.teal["400"],
        text: "analysis.source.search.overview.filters.maps-search",
    },
    "News Search": {
        color: colorsPalettes.torquoise["300"],
        text: "analysis.source.search.overview.filters.news-search",
    },
    "Shopping Search": {
        color: colorsPalettes.yellow["400"],
        text: "analysis.source.search.overview.filters.shopping-search",
    },
    "Video Search": {
        color: colorsPalettes.red["400"],
        text: "analysis.source.search.overview.filters.video-search",
    },
};

const numbers = "numbers";
const percents = "percents";
export const ALL_CHANNELS = "All Channels";

export const availableDataTypes: IDataType[] = [
    { title: "#", value: numbers },
    { title: "%", value: percents },
];

export interface IChannel {
    id: string;
    text: string;
    tooltip?: string;
}
export const ORGANIC_PAID_CHANNELS: IChannel[] = [
    {
        id: ALL_CHANNELS,
        text: "analysis.source.search.overview.filters.organicpaid",
    },
    {
        id: "Organic Search",
        text: "analysis.source.search.overview.filters.organic",
    },
    {
        id: "Paid Search",
        text: "analysis.source.search.overview.filters.paid",
    },
];

export const BRANDED_NON_BRANDED_CHANNELS: IChannel[] = [
    {
        id: ALL_CHANNELS,
        text: "analysis.source.search.overview.filters.brandednonbranded",
    },
    {
        id: "Branded",
        text: "analysis.source.search.overview.filters.branded",
    },
    {
        id: "Non Branded",
        text: "analysis.source.search.overview.filters.non-branded",
    },
];

const SearchTypesChannels = [
    {
        id: ALL_CHANNELS,
        text: "analysis.source.search.overview.filters.all-channels",
    },
];
